import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as LLMVendorModelsAPI from 'api/LLMVendorModels';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';
import { PAGES } from 'sections/LLMVendorModels/constants';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  selectedPage: PAGES.MAIN_PAGE,
  vendor: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'API_Key', key: 'api_key', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Base_Url', key: 'base_url', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  vendorModels: {
    selectedVendor: null,
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Model_Key', key: 'model_key', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getVendorData = createAsyncThunk('llmVendorModels/getVendorData', async ({ params, signal }) => {
  const res = await LLMVendorModelsAPI.getVendorData(params, signal);
  return res?.data?.data;
});

const createVendor = createAsyncThunk('llmVendorModels/createVendor', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.createVendor(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateVendor = createAsyncThunk('llmVendorModels/updateVendor', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.updateVendor(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteVendor = createAsyncThunk('llmVendorModels/deleteVendor', async (id) => {
  try {
    const res = await LLMVendorModelsAPI.deleteVendor(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

// The super-admin vendor-models endpoints are standard DRF
// ModelViewSets — list responses are ``{count, next, previous,
// results}`` directly on ``res.data`` (not wrapped in another
// ``data`` envelope like the tenant-level APIs). Reducers below read
// ``action.payload.count`` / ``action.payload.results``.
const getVendorModelsData = createAsyncThunk('llmVendorModels/getVendorModelsData', async ({ params, signal }) => {
  const res = await LLMVendorModelsAPI.getVendorModelsData(params, signal);
  return res?.data;
});

const createVendorModels = createAsyncThunk('llmVendorModels/createVendorModels', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.createVendorModels(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateVendorModels = createAsyncThunk('llmVendorModels/updateVendorModels', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.updateVendorModels(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteVendorModels = createAsyncThunk('llmVendorModels/deleteVendorModels', async (id) => {
  try {
    const res = await LLMVendorModelsAPI.deleteVendorModels(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const LLMVendorModelsSlice = createSlice({
  name: 'llmVendorModels',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    //Vendor Tab
    setVendorCurrentPage: (state, action) => {
      state.vendor.pagination.currentPage = action.payload;
    },
    setVendorPageSize: (state, action) => {
      state.vendor.pagination.pageSize = action.payload;
    },
    setSelectedVendorData: (state, action) => {
      state.vendorModels.selectedVendor = action.payload;
    },

    //Action Point Tab
    setVendorModelsCurrentPage: (state, action) => {
      state.vendorModels.pagination.currentPage = action.payload;
    },
    setVendorModelsPageSize: (state, action) => {
      state.vendorModels.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getVendorData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getVendorData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.vendor.pagination.totalRecords = action.payload?.count;
      state.vendor.data = action.payload?.results || [];
    },
    [getVendorData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createVendor.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateVendor.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.vendor.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.vendor.data = updatedData;
    },

    [updateVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteVendor.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getVendorModelsData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getVendorModelsData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.vendorModels.pagination.totalRecords = action.payload?.count;
      state.vendorModels.data = action.payload?.results || [];
    },
    [getVendorModelsData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createVendorModels.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateVendorModels.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.vendorModels.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.vendorModels.data = updatedData;
    },

    [updateVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteVendorModels.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const {
  resetState,
  setSelectedPage,
  setVendorCurrentPage,
  setVendorPageSize,
  setSelectedVendorData,
  setVendorModelsCurrentPage,
  setVendorModelsPageSize
} = LLMVendorModelsSlice.actions;

export default LLMVendorModelsSlice.reducer;

export {
  resetState,
  setSelectedPage,
  //Vendor
  setVendorCurrentPage,
  setVendorPageSize,
  getVendorData,
  createVendor,
  updateVendor,
  deleteVendor,
  setSelectedVendorData,
  //Vendor Models
  setVendorModelsCurrentPage,
  setVendorModelsPageSize,
  getVendorModelsData,
  createVendorModels,
  updateVendorModels,
  deleteVendorModels
};
